import { format } from "date-fns";
import { SafeText } from "../../components/SafeText";
import {
  isUrgentEntry,
  parseApiDate,
  toTitleCase,
  type CalendarDay,
  type CalendarEntry,
  type EntryType,
} from "./alertCalendarData";

export type CalendarView = "day" | "week" | "month";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Named rows a month cell shows before the rest collapse into "+N more". */
const MONTH_ROW_LIMIT = 3;

/* ── Review event card (week view) ──────────────────────────────────────── */

export function EventCard({
  entry,
  large = false,
  onSelect,
}: {
  entry: CalendarEntry;
  /** Day view shows a roomier card carrying org and owner */
  large?: boolean;
  onSelect: (entry: CalendarEntry) => void;
}) {
  const isNew = entry.entryType === "NEW_ALERT";
  const accent = isNew ? "border-l-violet-600" : "border-l-amber-600";
  const eyebrow = isNew
    ? "text-violet-700 dark:text-violet-400"
    : "text-amber-700 dark:text-amber-400";

  return (
    <button
      type="button"
      onClick={() => onSelect(entry)}
      className={`block w-full rounded-[9px] border border-border border-l-[3px] bg-card text-left shadow-sm transition-all hover:border-indigo-400 hover:shadow-md ${accent} ${
        large ? "mb-2.5 px-4 py-3.5" : "px-3 py-2.5"
      }`}
    >
      <span className="mb-1 flex items-center gap-2">
        <span className={`text-[9.5px] font-extrabold tracking-wider ${eyebrow}`}>
          {isNew ? "New alert" : "Review due"}
        </span>
        {/* Nothing to count down on the day itself — the eyebrow already
            says the review is due. */}
        {entry.daysRemaining > 0 && (
          <span className="ml-auto flex-none text-[10px] font-bold d">
            in {entry.daysRemaining}d
          </span>
        )}
      </span>
      <span
        className={`block font-semibold leading-tight text-foreground ${
          large ? "text-[14.5px]" : "truncate text-[13.5px]"
        }`}
      >
        {entry.title}
      </span>
      <span
        className={`mt-1 block text-[10.5px] d ${large ? "" : "truncate"}`}
      >
        {toTitleCase(entry.documentCategory)} - {toTitleCase(entry.documentType)}
        {large ? ` - ${entry.orgName}` : ""}
      </span>
    </button>
  );
}

/* ── Entry chip (week + month) ──────────────────────────────────────────── */

/* Week only ever chips reminders, so urgency drives its dot. Month can chip a
   mixed tail, where the type is the more useful signal. */
function dotTone(entry: CalendarEntry): string {
  if (entry.entryType === "REVIEW_DUE") return "bg-amber-600";
  if (entry.entryType === "NEW_ALERT") return "bg-violet-600";
  return isUrgentEntry(entry) ? "bg-amber-500" : "bg-blue-500";
}

function EntryChip({
  entries,
  count,
  label,
  compact = false,
  onOpenDay,
}: {
  entries: CalendarEntry[];
  count: number;
  label: string;
  /** Month cells are short, so the chip drops to row height. */
  compact?: boolean;
  onOpenDay: () => void;
}) {
  if (count === 0) return null;
  return (
    <button
      type="button"
      onClick={onOpenDay}
      className={`flex w-full items-center gap-2 rounded-[9px] border border-border bg-card font-semibold text-foreground transition-colors hover:border-indigo-400 hover:bg-indigo-500/10 hover:text-indigo-600 dark:hover:text-indigo-400 ${
        compact ? "px-1.5 py-1 text-[10.5px]" : "px-3 py-2.5 text-[12px]"
      }`}
    >
      <span className="flex flex-none gap-[3px]">
        {entries.slice(0, 4).map((e) => (
          <span
            key={`${e.entryType}-${e.documentId}`}
            className={`size-[7px] rounded-full ${dotTone(e)}`}
          />
        ))}
      </span>
      <span className="truncate">{label}</span>
    </button>
  );
}

/* ── Named rows (month view) ────────────────────────────────────────────── */

/* Tones match the page legend, so the swatch a user reads at the top of the
   grid is the same mark they see on a row. */
const MONTH_ROW_TONE: Record<EntryType, string> = {
  REVIEW_DUE:
    "border-l-[3px] border-amber-600 bg-amber-500/[0.14] text-amber-800 hover:bg-amber-500/[0.22] dark:text-amber-300",
  NEW_ALERT:
    "border-l-[3px] border-violet-600 bg-violet-500/[0.13] text-violet-800 hover:bg-violet-500/[0.2] dark:text-violet-300",
  REMINDER:
    "border border-dashed border-border d hover:border-indigo-400 hover:bg-indigo-500/10 hover:text-indigo-600 dark:hover:text-indigo-400",
};

/** One document, one line. Colour carries the type; the label repeats it for
    screen readers, and the title is available in full on hover. */
function MonthRow({
  entry,
  onSelect,
}: {
  entry: CalendarEntry;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={`${toTitleCase(entry.entryType)} - ${entry.title}`}
      className={`flex w-full items-center rounded-[7px] px-1.5 py-1 text-left text-[10.5px] font-semibold transition-colors ${MONTH_ROW_TONE[entry.entryType]}`}
    >
      <SafeText lines={1} tooltip={entry.title} className="min-w-0 flex-1">
        {entry.title}
      </SafeText>
    </button>
  );
}

/* ── Grid ───────────────────────────────────────────────────────────────── */

export function CalendarGrid({
  view,
  days,
  onSelect,
  onOpenDay,
}: {
  view: "month" | "week";
  days: CalendarDay[];
  onSelect: (entry: CalendarEntry, date: string) => void;
  onOpenDay: (date: string) => void;
}) {
  return (
    <div>
      {/* Weekday header — week view also shows the date */}
      <div className="grid grid-cols-7 border-b border-border bg-muted/40">
        {days.slice(0, 7).map((day, i) => {
          const highlight = view === "week" && day.today;
          return (
            <div
              key={day.date}
              className={`px-2.5 py-2 text-[10.5px] font-bold tracking-wide ${
                highlight ? "text-indigo-600 dark:text-indigo-400" : "d"
              }`}
            >
              {DOW[i]}
              {view === "week" && (
                <b
                  className={`ml-1.5 text-[13px] ${
                    highlight
                      ? "text-indigo-600 dark:text-indigo-400"
                      : "text-foreground"
                  }`}
                >
                  {format(parseApiDate(day.date), "d")}
                </b>
              )}
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-7">
        {days.map((day) => {
          const reviewDue = day.reviewDue ?? [];
          const newAlerts = day.newAlerts ?? [];
          const reminders = day.reminders ?? [];

          /* Both views name only the documents whose review falls due that
             day. New alerts and reminders each get their own chip, since a
             window opening is not the same as one counting down. Month caps
             the named rows to keep cells short; week has the height for all. */
          const namedLimit = view === "month" ? MONTH_ROW_LIMIT : reviewDue.length;
          const named = reviewDue.slice(0, namedLimit);
          const dueOverflow = reviewDue.slice(namedLimit);
          /* Counts fall back to the arrays so a payload reporting one without
             the other still offers a way into the day. */
          const newAlertCount = Math.max(day.newAlertCount, newAlerts.length);
          const reminderCount = Math.max(day.reminderCount, reminders.length);

          return (
            <div
              key={day.date}
              className={`flex flex-col gap-1.5 border-b border-r border-border/60 p-2 last:border-r-0 ${
                view === "month" ? "min-h-[132px]" : "min-h-[390px]"
              } ${day.inRange ? "" : "bg-muted/40"} ${
                day.today ? "bg-indigo-500/[0.06]" : ""
              }`}
            >
              {view === "month" && (
                <div className="mb-px">
                  {day.today ? (
                    <span className="inline-flex size-5 items-center justify-center rounded-md bg-primary text-[11.5px] font-semibold text-white">
                      {format(parseApiDate(day.date), "d")}
                    </span>
                  ) : (
                    <span
                      className={`text-[11.5px] font-semibold ${
                        day.inRange ? "d" : "text-muted-foreground/40"
                      }`}
                    >
                      {format(parseApiDate(day.date), "d")}
                    </span>
                  )}
                </div>
              )}

              {view === "month" ? (
                <>
                  {named.map((entry) => (
                    <MonthRow
                      key={`${entry.entryType}-${entry.documentId}`}
                      entry={entry}
                      onSelect={() => onSelect(entry, day.date)}
                    />
                  ))}
                  {/* One auto margin for the group — two would split the free
                      space between them and leave a gap. */}
                  <div className="mt-auto flex flex-col gap-1.5">
                    <EntryChip
                      entries={dueOverflow}
                      count={dueOverflow.length}
                      label={`${dueOverflow.length} Review due`}
                      compact
                      onOpenDay={() => onOpenDay(day.date)}
                    />
                    <EntryChip
                      entries={newAlerts}
                      count={newAlertCount}
                      label={`${newAlertCount} New alert${newAlertCount > 1 ? "s" : ""}`}
                      compact
                      onOpenDay={() => onOpenDay(day.date)}
                    />
                    <EntryChip
                      entries={reminders}
                      count={reminderCount}
                      label={`${reminderCount} Reminder${reminderCount > 1 ? "s" : ""}`}
                      compact
                      onOpenDay={() => onOpenDay(day.date)}
                    />
                  </div>
                </>
              ) : (
                <>
                  {day.totalCount === 0 && (
                    <div className="p-0.5 text-[10.5px] text-muted-foreground/40">-</div>
                  )}
                  {named.map((entry) => (
                    <EventCard
                      key={entry.documentId}
                      entry={entry}
                      onSelect={(e) => onSelect(e, day.date)}
                    />
                  ))}
                  {/* One auto margin for the pair — two would split the free
                      space between them and leave a gap. */}
                  <div className="mt-auto flex flex-col gap-1.5">
                    <EntryChip
                      entries={newAlerts}
                      count={newAlertCount}
                      label={`${newAlertCount} New alert${newAlertCount > 1 ? "s" : ""}`}
                      onOpenDay={() => onOpenDay(day.date)}
                    />
                    <EntryChip
                      entries={reminders}
                      count={reminderCount}
                      label={`${reminderCount} Reminder${reminderCount > 1 ? "s" : ""}`}
                      onOpenDay={() => onOpenDay(day.date)}
                    />
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
